import type { ComponentType } from 'preact'
import type { Store } from '../net/store'
import type { ProfileView } from '../profile/profileView'
import { useChunk } from '../ui/useChunk'
import { useStore } from '../ui/useStore'
import type { Account } from './account'
import type * as NoticeChunk from './AuthNotice'
import type * as SignInChunk from './SignInButtons'
import type { SignInButtonsProps } from './SignInButtons'

type SignInModule = typeof SignInChunk
type NoticeModule = typeof NoticeChunk

const signInChunk: { m: SignInModule | null } = { m: null }
const noticeChunk: { m: NoticeModule | null } = { m: null }
const loadSignIn = () => import('./SignInButtons')
const loadNotice = () => import('./AuthNotice')

/**
 * D-053 slot `signIn` (T2.6) as a synchronous component whose code arrives with the account:
 * nothing renders offline or before sign-in settles, so the buttons' chunk loads only then and
 * stays out of the menu's initial JS.
 */
export function makeLazySignIn(
  account: Store<Account | null>,
  profile: Store<ProfileView | null>,
): ComponentType<SignInButtonsProps> {
  return function SignInButtons({ full = false }: SignInButtonsProps) {
    const acc = useStore(account)
    const mod = useChunk(acc !== null, loadSignIn, signInChunk)
    return acc && mod ? <mod.SignInButtonsView account={acc} profile={profile} full={full} /> : null
  }
}

/** The account's notice and switch dialog, loaded the same way. */
export function makeLazyAuthNotice(account: Store<Account | null>): ComponentType {
  return function AuthNotice() {
    const acc = useStore(account)
    const mod = useChunk(acc !== null, loadNotice, noticeChunk)
    return acc && mod ? <mod.AuthNoticeView account={acc} /> : null
  }
}
